/**
 * Two people, two entry dates — the TIMING of a couple both entering care, and the words that
 * explain it (owner, 7 Oct 2026: "dad will go in straight away but mum might be ok for a little
 * while longer … people need to make decisions based on correct information").
 *
 * Where the second person enters later there are two stages, and the screens show both:
 *   stage 1 — until the second entry: one room paid for, the family home exempt from the aged
 *             care means test because the other person still lives in it, the couple assessed as
 *             illness-separated from the first entry;
 *   stage 2 — from the second entry: both rooms paid for, the home counted (up to the cap).
 * The projection has always run that timeline; this module is the one place the words for it live,
 * so step 2, the plan summary, the recap and the advice document all say the same thing.
 */
import { formatCurrency } from '../../utils/index.ts';

/** `YYYY-MM-DD` of today, local time. */
export function todayIso(now: Date = new Date()): string {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/**
 * A date field's value the browser has FINISHED: `YYYY-MM-DD` with a year that could be real. A
 * native date input reports "0002-01-01" while "2029" is being typed, and anything that acts on the
 * value as it changes must ignore those (owner-caught, 7 Oct 2026: a half-typed date swapped the
 * rooms and was then kept as 01/01/0002).
 */
export function isCompleteIsoDate(value: string | undefined, now: Date = new Date()): boolean {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const year = Number(value.slice(0, 4));
    return year >= now.getFullYear() - 1 && year <= now.getFullYear() + 60;
}

/** True where the second person's entry is after the first's (a blank first date is "now"). */
export function secondEntersLater(firstDate: string | undefined, secondDate: string | undefined, now: Date = new Date()): boolean {
    if (!isCompleteIsoDate(secondDate, now)) return false;
    return secondDate! > (firstDate || todayIso(now));
}

/** True where the second room's date is BEFORE the first's: that person moves in first. */
export function secondEntersEarlier(firstDate: string | undefined, secondDate: string | undefined, now: Date = new Date()): boolean {
    if (!isCompleteIsoDate(secondDate, now)) return false;
    return secondDate! < (firstDate || todayIso(now));
}

/** "February 2029" — the month is enough for a reader; the day belongs to the date field. */
export function phaseFromLabel(iso: string): string {
    const m = /^(\d{4})-(\d{2})/.exec(iso || '');
    if (!m) return iso;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

/** The two people by ROLE — whoever moves in first leads, whichever of the couple that is. */
export function roleNames(
    factFind: { client?: { firstName?: string }; partner?: { firstName?: string } },
    secondPerson: 'client' | 'partner' | undefined,
    fallbacks: { first: string; second: string } = { first: 'the client', second: 'their partner' },
): { firstName: string; secondName: string } {
    const second = secondPerson === 'client' ? factFind.client : factFind.partner;
    const first = secondPerson === 'client' ? factFind.partner : factFind.client;
    return { firstName: (first?.firstName || '').trim() || fallbacks.first, secondName: (second?.firstName || '').trim() || fallbacks.second };
}

export interface PhaseNames {
    firstName: string;
    secondName: string;
    /** "February 2029" */
    fromLabel: string;
}

/** The one explanation of the two stages, used under step 2's heading. */
export function phasesIntro({ firstName, secondName, fromLabel }: PhaseNames): string {
    return `${firstName} moves in first; ${secondName} stays at home until ${fromLabel}. Until then only ${firstName}'s room is paid for, and the family home stays out of the aged care means test because ${secondName} lives there. From ${fromLabel} both rooms are paid for and the home counts, up to the capped value. The numbers below show both stages.`;
}

/** Heading for the second-stage row of numbers. */
export function phaseTwoHeading({ fromLabel }: Pick<PhaseNames, 'fromLabel'>): string {
    return `From ${fromLabel} — both in care`;
}

/** Caption under the first-stage care figure. */
export function phaseOneCaption({ firstName, fromLabel }: Pick<PhaseNames, 'firstName' | 'fromLabel'>): string {
    return `Until ${fromLabel} — ${firstName}'s room only.`;
}

/** The plan summary's one-line note under the first-stage daily cost. */
export function railPhaseTwoLine({ secondName, fromLabel }: Pick<PhaseNames, 'secondName' | 'fromLabel'>, dailyTotal: number): string {
    return `From ${fromLabel}, when ${secondName} moves in: ${formatCurrency(Math.round(dailyTotal))} a day (${formatCurrency(Math.round(dailyTotal * 365))} a year) for both rooms.`;
}

/** Step 2's "what it would cost" beat, for the staggered case. */
export function careBeatStaggered(
    { firstName, secondName, fromLabel }: PhaseNames,
    figures: { firstDaily: number; phaseTwoFirstDaily: number; secondDaily: number },
): { untilThen: string; fromThen: string } {
    const both = figures.phaseTwoFirstDaily + figures.secondDaily;
    return {
        untilThen: `it would cost ${formatCurrency(figures.firstDaily)} a day (${formatCurrency(figures.firstDaily * 365)}/yr) for ${firstName}'s room until ${fromLabel}.`,
        fromThen: `From ${fromLabel}, with ${secondName} in care too, ${formatCurrency(both)} a day (${formatCurrency(both * 365)}/yr) for the two rooms together — ${formatCurrency(figures.phaseTwoFirstDaily)} for ${firstName}'s and ${formatCurrency(figures.secondDaily)} for ${secondName}'s.`,
    };
}

/** The gap beat's extra sentence for the second stage. */
export function gapPhaseTwoLine({ secondName, fromLabel }: Pick<PhaseNames, 'secondName' | 'fromLabel'>, gapPhaseTwo: number): string {
    if (gapPhaseTwo >= 0) return `From ${fromLabel}, with ${secondName}'s room paid for too, income still covers everything.`;
    return `From ${fromLabel}, with ${secondName}'s room paid for too, the gap grows to ${formatCurrency(Math.abs(Math.round(gapPhaseTwo)))} a year.`;
}

/** Step 1's explanation of the second living-costs figure, and the note's sentence on it. */
export function livingPhaseTwoHelp({ firstName, secondName, fromLabel }: PhaseNames): string {
    return `Living costs usually change again once ${secondName} moves in: with nobody at home, the home's running costs stop and the care home covers meals and utilities. Enter what you expect the household to spend a year from ${fromLabel}; leave it as it is if nothing changes. Until then, the figure above applies — ${firstName} in care, ${secondName} at home.`;
}
export function livingPhaseTwoLine({ fromLabel }: Pick<PhaseNames, 'fromLabel'>, amount: number): string {
    return `From ${fromLabel}, with both in care, living costs are set to ${formatCurrency(amount)} a year.`;
}

/** The plan recap's and advice document's sentence on the two stages. */
export function recapPhaseLine({ firstName, secondName, fromLabel }: PhaseNames, firstDaily: number, phaseTwoDaily: number): string {
    return `Until ${fromLabel} only ${firstName}'s room is paid for (about ${formatCurrency(Math.round(firstDaily))} a day). From ${fromLabel}, when ${secondName} moves in, the plan pays for both rooms: about ${formatCurrency(Math.round(phaseTwoDaily))} a day.`;
}
